import React, { useState } from 'react';

const privacySections = [
  {
    id: 'overview',
    number: '01',
    title: 'Privacy Commitment & Overview',
    content: (
      <>
        <p className="terms-clause-text">
          At <strong>Experts Communication</strong> ("Company", "we", "us", or "our"), safeguarding your privacy and ensuring the confidentiality of your personal and business data is a core organizational priority. This Privacy Policy outlines how we collect, process, protect, and utilize information gathered across our website, communication channels, and client service portals.
        </p>
        <p className="terms-clause-text">
          By utilizing our website or engaging our services in Social Media Marketing, SEO, Web Development, BPO, or Managed IT Solutions, you acknowledge and consent to the data practices described herein.
        </p>
        <div className="terms-callout-box">
          <strong>Fundamental Guarantee:</strong> We do not sell, rent, or monetize your personal or client data to third-party data brokers under any circumstances.
        </div>
      </>
    )
  },
  {
    id: 'information-collected',
    number: '02',
    title: 'Information We Collect',
    content: (
      <>
        <p className="terms-clause-text">
          Depending on how you interact with Experts Communication, we may collect the following categories of information:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Direct Contact Data:</strong> Name, professional email address, phone number, company name, job title, and project requirements submitted via contact forms, quote requests, or audit inquiries.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Technical & Usage Metrics:</strong> IP address, browser type, operating system, referring URLs, device fingerprints, time spent on pages, and navigation paths collected through secure analytics scripts.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Client Project Assets:</strong> Brand guidelines, campaign media, credentials shared under NDA for website optimization, social account management, or server deployment.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'data-utilization',
    number: '03',
    title: 'How We Utilize Your Information',
    content: (
      <>
        <p className="terms-clause-text">
          All collected information is used strictly for legitimate business purposes, including:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Delivering, executing, and refining customized digital marketing campaigns, web architectures, and SEO audits.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Processing billing, project milestone invoicing, and contractual documentation.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Communicating project sprint updates, technical audits, performance reports, and customer service responses.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Monitoring website infrastructure health, preventing malicious attacks, and enhancing user experience.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'security-storage',
    number: '04',
    title: 'Data Security & Storage Standards',
    content: (
      <>
        <p className="terms-clause-text">
          We employ multi-tiered technical and administrative safeguards to protect your data against unauthorized access, loss, or alteration:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>256-Bit SSL/TLS Encryption:</strong> All data transmitted between your browser and our servers is encrypted using modern cipher suites.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Role-Based Access Control (RBAC):</strong> Client credentials and campaign assets are restricted strictly to authorized project engineers and account leads bound by NDAs.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Regular Security Audits:</strong> Periodic penetration testing, malware scans, and database integrity verifications.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'third-party-sharing',
    number: '05',
    title: 'Third-Party Disclosures & Integrations',
    content: (
      <>
        <p className="terms-clause-text">
          We may share limited data with vetted third-party service providers solely to facilitate project operations:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Analytics & Advertising Platforms:</strong> Google Analytics, Meta Ads Manager, and LinkedIn Campaign Manager for campaign execution and reporting.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Cloud Infrastructure Providers:</strong> Secure enterprise hosting partners with global SOC-2 and ISO certifications.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Legal Mandates:</strong> Disclosure may occur if required by law enforcement or judicial order under the laws of Pakistan.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'cookies-tracking',
    number: '06',
    title: 'Cookies & Tracking Technologies',
    content: (
      <>
        <p className="terms-clause-text">
          Our website uses standard cookies and tracking pixels to analyze traffic patterns, personalize content, and optimize page load speeds.
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Essential Cookies:</strong> Required for site navigation, form security, and core technical functionalities.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Analytical Cookies:</strong> Help us measure visitor demographics and traffic volume to improve web architecture.</span>
          </li>
        </ul>
        <div className="terms-callout-box">
          <strong>Managing Cookies:</strong> You can configure your browser settings to reject cookies or alert you when cookies are placed. Note that disabling cookies may affect certain interactive features.
        </div>
      </>
    )
  },
  {
    id: 'user-rights',
    number: '07',
    title: 'Your Privacy Rights & Controls',
    content: (
      <>
        <p className="terms-clause-text">
          In alignment with international data privacy frameworks (including GDPR principles and CCPA standards), you hold the following rights:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Right to Access & Portability:</strong> Request an export of your personal information held in our records.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Right to Rectification:</strong> Request prompt correction of inaccurate or outdated contact information.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Right to Erasure ("To Be Forgotten"):</strong> Request full deletion of your personal records and past inquiry history.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Opt-Out of Marketing:</strong> Unsubscribe from marketing newsletters or promotional communications at any time.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'data-retention',
    number: '08',
    title: 'Data Retention & Purge Protocols',
    content: (
      <>
        <p className="terms-clause-text">
          We retain personal data and project records only for as long as necessary to fulfill contractual obligations, comply with tax/legal retention mandates, or resolve active disputes.
        </p>
        <p className="terms-clause-text">
          Upon project conclusion or upon verified erasure requests, all proprietary client assets and stored credentials are securely wiped in accordance with cryptographic erasure protocols.
        </p>
      </>
    )
  },
  {
    id: 'privacy-contact',
    number: '09',
    title: 'Privacy Inquiries & Data Protection Officer',
    content: (
      <>
        <p className="terms-clause-text">
          If you have questions, data access requests, or concerns regarding our privacy standards, please reach out to our dedicated privacy compliance team:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Official Email:</strong> <a href="mailto:info@expertscommunication.net" style={{ color: '#c40a0a', textDecoration: 'underline' }}>info@expertscommunication.net</a></span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Corporate Address:</strong> KDA Commercial Building, Office # 3, First Floor, North Nazimabad, Block A, Karachi, Pakistan</span>
          </li>
        </ul>
      </>
    )
  }
];

function PrivacyPolicyPage() {
  const [activeNav, setActiveNav] = useState('overview');

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 160;
      for (const section of privacySections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveNav(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToClause = (id) => {
    setActiveNav(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -110;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = '/contact/';
  };

  return (
    <div className="terms-page-wrapper">
      {/* Hero Header - Matching SEO Hero Dark Aesthetic */}
      <section className="terms-hero-section">
        <div className="container">
          <h1 className="terms-hero-title">
            Privacy <span style={{ color: '#c40a0a' }}>Policy</span>
          </h1>

          <p className="terms-hero-subtitle">
            Your privacy is of utmost importance to us. Learn how Experts Communication collects, safeguards, and respects your personal data across all digital touchpoints and engagements.
          </p>

          {/* Quick Highlight Cards */}
          <div className="terms-highlights-grid">
            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Zero Data Selling</h4>
                <p>We never monetize client data</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>256-Bit SSL Security</h4>
                <p>Encrypted data transfers</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Global Privacy Standards</h4>
                <p>GDPR &amp; CCPA aligned protocols</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 14 14" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Updated Version</h4>
                <p>September 2026 Edition</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Body Section with Sticky TOC Sidebar */}
      <section className="terms-body-section">
        <div className="container">
          <div className="terms-layout-grid">
            {/* Left Sticky Table of Contents */}
            <aside className="terms-toc-sidebar">
              <h3 className="terms-toc-title">Privacy Contents</h3>
              <nav className="terms-toc-nav">
                {privacySections.map((item) => (
                  <button
                    key={item.id}
                    className={`terms-toc-link ${activeNav === item.id ? 'active' : ''}`}
                    onClick={() => scrollToClause(item.id)}
                  >
                    <span style={{ color: '#c40a0a', fontWeight: '800' }}>{item.number}.</span>
                    <span>{item.title}</span>
                  </button>
                ))}
              </nav>
            </aside>

            {/* Right Clause Details */}
            <main className="terms-content-area">
              {privacySections.map((section) => (
                <div key={section.id} id={section.id} className="terms-clause-card">
                  <div className="terms-clause-header">
                    <div className="terms-clause-number">{section.number}</div>
                    <h2 className="terms-clause-title">{section.title}</h2>
                  </div>
                  {section.content}
                </div>
              ))}

              {/* Privacy Support & Data Request Box */}
              <div className="terms-support-card">
                <h3 className="terms-support-title">Need to Exercise Your Data Rights?</h3>
                <p className="terms-support-desc">
                  To request a copy of your personal data, submit an erasure request, or ask questions about our data protection standards, contact our privacy compliance office.
                </p>
                <div className="terms-support-actions">
                  <button className="btn-terms-contact" onClick={handleContactClick}>
                    <span>Submit Privacy Request</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                  <a href="mailto:info@expertscommunication.net" className="btn-terms-email">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <span>info@expertscommunication.net</span>
                  </a>
                </div>
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicyPage;
